import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

import { useInvalidateAccountBilling } from '../../allFeatures/AccountBilling/useInvalidateAccountBilling';
import { useOrganizationContext } from '../../context';
import { queryKeys } from '../../queryKeys';

/** Refresh billing readers once setup or a polled settlement has completed. */
export function useRefreshOrganizationBilling() {
  const queryClient = useQueryClient();
  const invalidateAccountBilling = useInvalidateAccountBilling();
  const { reconcileOrganizationRow } = useOrganizationContext();

  return React.useCallback(
    async (organizationId: string) => {
      const { queryFn, queryKey } =
        queryKeys.organizations.fetchOrganizationById({ id: organizationId });
      // fetchQuery deduplicates an in-flight request even with staleTime: 0.
      // Discard a pre-settlement response before fetching the new lock state.
      await queryClient.cancelQueries({ exact: true, queryKey });
      await Promise.all([
        invalidateAccountBilling(),
        queryClient
          .fetchQuery({ queryFn, queryKey, staleTime: 0 })
          .then((organization) => reconcileOrganizationRow(organization))
          .catch(() => undefined),
        queryClient.invalidateQueries({
          exact: false,
          queryKey:
            queryKeys.organizations.fetchInfiniteSwitcherOrganizations._def,
        }),
        // The per-organization lock as `useCrossOrganizationReadableMemberships`
        // and the profile switcher read it — without this a just-unlocked
        // organization stays skipped by every cross-organization fan-out.
        queryClient.invalidateQueries({
          queryKey: queryKeys.users.fetchLogicalProfiles().queryKey,
        }),
      ]);
    },
    [invalidateAccountBilling, queryClient, reconcileOrganizationRow],
  );
}
